import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertCircle } from 'lucide-react';
import StarRating from './StarRating';

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    score: 0,
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleScoreChange = (score) => {
    setFormData(prev => ({
      ...prev,
      score
    }));
    // 清除错误信息
    if (error) setError('');
  };

  const handleContentChange = (comment) => {
    setFormData(prev => ({
      ...prev,
      comment
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证表单
    if (formData.score === 0) {
      setError('请选择一个评分');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // 这里可以调用API提交反馈
      // await new Promise(resolve => setTimeout(resolve, 1000)); // 模拟API调用
      
      if (onSubmit) {
        await onSubmit(formData);
      }
      
      // 重置表单
      setFormData({
        score: 0,
        comment: ''
      });
      
      onClose();
    } catch (err) {
      setError('提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        score: 0,
        comment: ''
      });
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>提供反馈</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ModalSubtitle>您的体验如何？请告诉我们您的想法。</ModalSubtitle>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>选择您的评分</Label>
              <StarRating
                value={formData.score}
                onChange={handleScoreChange}
                maxStars={5}
                size={48}
                showValue={false}
              />
            </FormGroup>

            <FormGroup>
              <Label>告诉我们您的体验</Label>
              <Textarea
                placeholder=""
                value={formData.comment}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={6}
                maxLength={500}
              />
              <CharCount>{formData.comment.length}/500</CharCount>
            </FormGroup>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                <span>{error}</span>
              </ErrorMessage>
            )}

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? '提交中...' : '提交反馈'}
            </SubmitButton>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// 样式组件
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 12px 16px 16px;
  overflow-y: auto;
  flex: 1;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;



const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CharCount = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  margin-top: 4px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: #6366f1;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #5856eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export default FeedbackModal; 